/**
 * Location / geo search debug logging for AlgoliaSearchPage.
 *
 * Enabled when:
 *   - NODE_ENV === 'development', OR
 *   - localStorage.debugAlgoliaLocation === '1'
 *
 * Toggle in browser console:
 *   localStorage.setItem('debugAlgoliaLocation', '1'); location.reload();
 *   localStorage.removeItem('debugAlgoliaLocation'); location.reload();
 */

const DEBUG_PREFIX = '[AlgoliaLocation]';
let searchRequestCounter = 0;
let hasPrintedEnableHint = false;

export const isAlgoliaLocationDebugEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    process.env.NODE_ENV === 'development' ||
    window.localStorage.getItem('debugAlgoliaLocation') === '1'
  );
};

export const printAlgoliaLocationDebugHint = () => {
  if (!isAlgoliaLocationDebugEnabled() || hasPrintedEnableHint) {
    return;
  }
  hasPrintedEnableHint = true;
  // eslint-disable-next-line no-console
  console.info(
    `${DEBUG_PREFIX} Debug logging is ON. Paste logs prefixed with "${DEBUG_PREFIX}" when reporting issues.`
  );
  // eslint-disable-next-line no-console
  console.info(
    `${DEBUG_PREFIX} Disable: localStorage.removeItem('debugAlgoliaLocation'); location.reload();`
  );
};

/** Safe copy for console — avoids huge nested objects. */
export const summarizeAlgoliaRequest = request => {
  const params = request?.params ?? {};
  return {
    indexName: request?.indexName,
    query: params.query ?? '',
    page: params.page,
    filters: params.filters,
    facetFilters: params.facetFilters,
    aroundLatLng: params.aroundLatLng,
    aroundRadius: params.aroundRadius,
    hitsPerPage: params.hitsPerPage,
  };
};

export const summarizeAlgoliaResult = result => ({
  index: result?.index,
  nbHits: result?.nbHits,
  nbPages: result?.nbPages,
  page: result?.page,
  processingTimeMS: result?.processingTimeMS,
  query: result?.query,
  params: result?.params,
  firstHitIds: (result?.hits || []).slice(0, 3).map(h => h.objectID),
  firstHitTitles: (result?.hits || []).slice(0, 3).map(h => h.title),
  firstHitLocations: (result?.hits || [])
    .slice(0, 3)
    .map(h => h.publicData?.location || h._geoloc || null),
});

export const logAlgoliaLocationDebug = (event, payload = {}) => {
  if (!isAlgoliaLocationDebugEnabled()) {
    return;
  }
  const entry = {
    ts: new Date().toISOString(),
    event,
    ...payload,
  };
  // eslint-disable-next-line no-console
  console.log(`${DEBUG_PREFIX} ${event}`, entry);
};

export const logAlgoliaSearchClientRequest = ({
  requests,
  transformedRequests,
  locationContext,
  phase,
}) => {
  if (!isAlgoliaLocationDebugEnabled()) {
    return null;
  }
  searchRequestCounter += 1;
  const id = searchRequestCounter;
  logAlgoliaLocationDebug(`search-client:${phase}`, {
    requestId: id,
    locationContext: { ...locationContext },
    requestCount: requests?.length,
    requests: (requests || []).map(summarizeAlgoliaRequest),
    transformedRequests: transformedRequests
      ? transformedRequests.map(summarizeAlgoliaRequest)
      : undefined,
  });
  return id;
};

export const logAlgoliaSearchClientResponse = (requestId, response) => {
  if (!isAlgoliaLocationDebugEnabled()) {
    return;
  }
  logAlgoliaLocationDebug('search-client:response', {
    requestId,
    results: (response?.results || []).map(summarizeAlgoliaResult),
  });
};

export const logAlgoliaSearchClientError = (requestId, error) => {
  if (!isAlgoliaLocationDebugEnabled()) {
    return;
  }
  logAlgoliaLocationDebug('search-client:error', {
    requestId,
    message: error?.message,
    error,
  });
};

/** One-line snapshot you can paste — call from LocationSearchDebugWatcher. */
export const logAlgoliaLocationSnapshot = snapshot => {
  logAlgoliaLocationDebug('snapshot', snapshot);
};

/** Expose on window for manual trigger: dumpAlgoliaLocationDebug() in console */
export const registerAlgoliaLocationDebugGlobals = () => {
  if (typeof window === 'undefined' || !isAlgoliaLocationDebugEnabled()) {
    return;
  }
  window.dumpAlgoliaLocationDebugHelp = () => {
    // eslint-disable-next-line no-console
    console.info(`
${DEBUG_PREFIX} How to capture logs for debugging:
1. Open DevTools → Console
2. Filter by: AlgoliaLocation
3. Go to /s/services
4. Type "Jeddah" in location field → wait 1 second
5. Click "Near me" → allow GPS → wait 1 second
6. Right-click console → Save as... OR copy all [AlgoliaLocation] lines

Key events to include:
  - input:location-debounced
  - location-sync:refresh-called
  - search-client:location-appended-to-query (or passthrough-near-me)
  - search-client:response (check nbHits)
  - snapshot (compare ui.nbHits vs response nbHits)
`);
  };
};
